import { Type } from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { TIME_REGEX } from '@app/app.constants';
import { C5EmailValidator } from '@app/shared/validators/c5-email-validator';
import { UniqueValidatorFactory, UniqueValueFunction } from '@app/shared/validators/unique-validator';
import {
  ArrayMergeFilterFunction,
  ArrayMergeFunction,
  BooleanMergeFunction,
  IntMergeFunction,
  IsoTimeMergeFunction,
  LocalMergeFunction,
  MergeFunction,
  TimeNormalise,
} from '@app/utils/form-mapping/helpers/default-merge-functions';
import { ArrayParseFunction, ParseFunction } from '@app/utils/form-mapping/helpers/default-parse-functions';
import { NotBlankValidator, RequireAnyValidator } from '@app/utils/form-mapping/helpers/default-validators';
import { mergeMaybeEmptyField, parseMaybeEmptyField } from '@app/utils/maybe-empty-field';
import {
  FormMappingAttribute,
  FormMappingAttributeDef,
  PortalDecorator,
} from '../decorators/form-mapping-attribute';
import { FormMappingModel } from '../models/form-mapping-model';

/**
 * Form attributes with this decorators are not serialised when creating the destination object that will be sent to the backend
 */
export const ArrayInput = <T extends FormMappingModel>(
  type: Type<T>,
  options: {
    allowDuplicates: boolean;
    filterFunction?: ArrayMergeFilterFunction<T>;
    mergeFunction?: MergeFunction<any>;
    parseFunction?: ParseFunction<any>;
    selector?: string;
    uniqueValueFunction?: UniqueValueFunction<T>;
  } = { allowDuplicates: true }
) => {
  const attributeOptions: FormMappingAttributeDef = {
    isArray: true,
    mergeFunction: options.mergeFunction || ArrayMergeFunction(type, options.filterFunction),
    parseFunction: options.parseFunction || ArrayParseFunction(type),
    selector: options.selector,
  };

  if (options.allowDuplicates === false) {
    if (options.uniqueValueFunction === undefined) {
      throw new Error('If no duplicates are allowed then a uniqueValueFunction has to be defined!');
    }

    attributeOptions.validators = [UniqueValidatorFactory(options.uniqueValueFunction)];
  }

  return FormMappingAttribute(attributeOptions);
};

export function BooleanInput(options: FormMappingAttributeDef | string): PortalDecorator;
export function BooleanInput<T>(instance: T, attribute: string): void;
export function BooleanInput<T>(
  _: FormMappingAttributeDef | string | T,
  attribute?: string
): void | PortalDecorator {
  let returnValue: void | PortalDecorator;

  if (attribute !== undefined) {
    // Called as expression
    returnValue = FormMappingAttribute({
      mergeFunction: BooleanMergeFunction,
      selector: attribute,
    })(_, attribute);
  } else if (typeof _ === 'string') {
    returnValue = FormMappingAttribute({
      mergeFunction: BooleanMergeFunction,
      selector: _,
    });
  } else {
    returnValue = FormMappingAttribute({
      mergeFunction: BooleanMergeFunction,
      ...(_ as any),
    });
  }

  return returnValue;
}

export const Email = FormMappingAttribute({
  validators: [C5EmailValidator],
});

export const MaybeEmptyInput = FormMappingAttribute({
  mergeFunction: mergeMaybeEmptyField,
  parseFunction: parseMaybeEmptyField,
});

export const GroupInput = <T extends FormMappingModel>(
  type: Type<T>,
  formMappingAttributeDef?: FormMappingAttributeDef
) => {
  function formGroupFunction(): UntypedFormGroup {
    return type.prototype.formGroup();
  }

  function mergeFunction(model, value, attribute, form) {
    model[attribute] = type.prototype.merge(model[attribute], form[attribute]);
  }

  function parseFunction(value) {
    return type.prototype.from(value);
  }

  const attributeOptions: FormMappingAttributeDef = {
    formGroupFunction,
    isGroup: true,
    mergeFunction,
    parseFunction,
  };

  if (formMappingAttributeDef !== undefined) {
    if (formMappingAttributeDef.mergeFunction !== undefined) {
      attributeOptions.mergeFunction = formMappingAttributeDef.mergeFunction;
    }

    if (formMappingAttributeDef.parseFunction !== undefined) {
      attributeOptions.parseFunction = formMappingAttributeDef.parseFunction;
    }
  }

  return FormMappingAttribute(attributeOptions);
};

export function IntInput(options: FormMappingAttributeDef | string): PortalDecorator;
export function IntInput<T>(instance: T, attribute: string): void;
export function IntInput<T>(
  _: FormMappingAttributeDef | string | T,
  attribute?: string
): void | PortalDecorator {
  let returnValue: void | PortalDecorator;

  if (attribute !== undefined) {
    // Called as expression
    returnValue = FormMappingAttribute({
      mergeFunction: IntMergeFunction,
      selector: attribute,
    })(_, attribute);
  } else if (typeof _ === 'string') {
    returnValue = FormMappingAttribute({
      mergeFunction: IntMergeFunction,
      selector: _,
    });
  } else {
    returnValue = FormMappingAttribute({
      mergeFunction: IntMergeFunction,
      ...(_ as any),
    });
  }

  return returnValue;
}

export function Local(options: FormMappingAttributeDef): PortalDecorator;
export function Local<T>(instance: T, attribute: string): void;
export function Local<T>(_: FormMappingAttributeDef | T, attribute?: string): void | PortalDecorator {
  let returnValue: void | PortalDecorator;

  if (attribute !== undefined) {
    // Called as expression
    returnValue = FormMappingAttribute({
      mergeFunction: LocalMergeFunction,
      selector: attribute,
    })(_, attribute);
  } else {
    returnValue = FormMappingAttribute({
      mergeFunction: LocalMergeFunction,
      ...(_ as any),
    });
  }

  return returnValue;
}

export const Max = (max: number) =>
  FormMappingAttribute({
    validators: [Validators.max(max)],
  });

export const MaxLength = (maxLength: number) =>
  FormMappingAttribute({
    validators: [Validators.maxLength(maxLength)],
  });

export const Min = (min: number) =>
  FormMappingAttribute({
    validators: [Validators.min(min)],
  });

export const MinLength = (minLength: number) =>
  FormMappingAttribute({
    validators: [Validators.minLength(minLength)],
  });

export const NotBlank = FormMappingAttribute({
  validators: [NotBlankValidator],
});

export const Pattern = (pattern: string | RegExp) =>
  FormMappingAttribute({
    validators: [Validators.pattern(pattern)],
  });

export const PhoneNumber = FormMappingAttribute({
  normaliseInput(value?: string): string {
    return (value || '').replace(/^00/, '+');
  },
  validators: [Validators.pattern(/^\+\d+$/)],
});

export const RequireAny = (...name: string[]) =>
  FormMappingAttribute({
    validators: [RequireAnyValidator(...name)],
  });

export const Required = FormMappingAttribute({
  validators: [NotBlankValidator, Validators.required],
});

export function StringInput(options: FormMappingAttributeDef | string): PortalDecorator;
export function StringInput<T>(instance: T, attribute: string): void;
export function StringInput<T>(
  _: FormMappingAttributeDef | string | T,
  attribute?: string
): void | PortalDecorator {
  let returnValue: void | PortalDecorator;

  if (attribute !== undefined) {
    // Called as expression
    returnValue = FormMappingAttribute({
      selector: attribute,
    })(_, attribute);
  } else if (typeof _ === 'string') {
    returnValue = FormMappingAttribute({
      selector: _,
    });
  } else {
    returnValue = FormMappingAttribute({
      ...(_ as any),
    });
  }

  return returnValue;
}

export const Time = FormMappingAttribute({
  mergeFunction: IsoTimeMergeFunction,
  normaliseInput: TimeNormalise,
  validators: [Validators.pattern(TIME_REGEX)],
});
